import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import AppDialog from '@tenant/core/App/Dialog/AppDialog';
import EditIcon from '@mui/icons-material/Edit';
import useModal from '@tenant/utility/hooks/useModal';
import { Box, Button, IconButton } from '@mui/material';
import MarketplaceAppSelect from '@tenant/core/App/Select/MarketplaceAppSelect';
import useUpdateMappings from 'redux/slices/appInstance/hooks/useUpdateMappings';
import { LoadingButton } from '@mui/lab';

const UpdateMarketplaceApps = ({
  appInstanceId,
  defaultApps,
  refetchDetail,
}) => {
  const { visible, onClose, onShow } = useModal();
  const [selectedApps, setSelectedApps] = useState(defaultApps ?? []);

  const isDirty = useMemo(() => {
    if (!selectedApps || !defaultApps) return false;
    if (selectedApps.length !== defaultApps.length) return true;
    return selectedApps.some(
      (selected) => !defaultApps.find((def) => def.Id === selected.Id),
    );
  }, [selectedApps, defaultApps]);

  const mutation = useUpdateMappings();

  return (
    <>
      <IconButton size='small' onClick={onShow}>
        <EditIcon fontSize='inherit' />
      </IconButton>

      <AppDialog
        open={visible}
        title='Update Marketplace Apps'
        dialogProps={{ fullWidth: true }}
        dialogFooterElement={
          <Box
            display='flex'
            justifyContent='end'
            gap={2}
            pl={6}
            pr={6}
            pt={3}
            pb={3}
            sx={{
              '& .MuiButton-root': {
                minWidth: 80,
                boxShadow: 'none',
                '&.cancel': {
                  backgroundColor: '#efefef',
                  color: '#545454',
                  '&:hover': {
                    backgroundColor: '#dcdcdc',
                  },
                },
              },
            }}
          >
            <Button
              variant='contained'
              className='cancel'
              disabled={mutation.isPending}
              onClick={onClose}
            >
              Cancel
            </Button>
            <LoadingButton
              disabled={!isDirty}
              loading={mutation.isPending}
              variant='contained'
              onClick={async () => {
                await mutation.mutateAsync({
                  appInstanceId,
                  marketplaceApps: selectedApps?.map((e) => e.Id),
                });

                onClose();
                refetchDetail?.();
              }}
            >
              Save
            </LoadingButton>
          </Box>
        }
        handleClose={onClose}
      >
        <MarketplaceAppSelect
          isMultiple
          limitTags={-1}
          value={selectedApps ?? []}
          initialData={defaultApps ?? []}
          onChange={(e, value) => {
            setSelectedApps(value);
          }}
        />
      </AppDialog>
    </>
  );
};

export default React.memo(UpdateMarketplaceApps);

UpdateMarketplaceApps.propTypes = {
  appInstanceId: PropTypes.string,
  defaultApps: PropTypes.array,
  refetchDetail: PropTypes.func,
};
